import {
  call,
  put, takeLatest,
} from 'redux-saga/effects';
import {
  convertPolygonToGeometry, removeGeometryPolygonIntersections,
} from '~/_shared/types/polygon/polygon.utils';
import { flatten } from '~/_shared/utils/collections/collections';
import { boundaryItemsUpdateItem } from '~/store/boundaryItems/boundaryItems.actionCreators';
import type { MapIdState } from '~/store/mapId/mapId.state';
import { clientIdSelector } from '~/store/selectors/useClientIdSelector';
import { mapIdSelector } from '~/store/selectors/useMapIdSelector';
import { select } from '../../../../../_shared/utils/saga/effects';
import { type PickAction } from '../../../../../_shared/utils/types/action.type';
import {
  type BoundaryCombineGeometryRequest,
  type BoundaryCombineGeometryResponse,
  bulkUpdateBoundaryGeometry,
  getBoundaryCombineGeometry,
} from '../../../../boundaries/boundaries.repository';
import { createBoundaryStateItemFromBoundaryResponse } from '../../../../boundaryItems/boundaryItems.factory';
import { type BoundaryDragEditAction } from './boundaryPolygonUpdate.action';
import {
  boundaryPolygonSaveError,
  boundaryPolygonSaveFinished,
  boundaryPolygonSaveStarted,
} from './boundaryPolygonUpdate.actionCreators';
import { BOUNDARY_POLYGON_UPDATE_REQUEST } from './boundaryPolygonUpdate.actionTypes';

export function* boundariesEditingSagas() {
  yield takeLatest(BOUNDARY_POLYGON_UPDATE_REQUEST, updateBoundary);
}

function* updateBoundary(action: PickAction<BoundaryDragEditAction, typeof BOUNDARY_POLYGON_UPDATE_REQUEST>) {
  const clientId: number | null = yield select<number | null>(clientIdSelector);
  const mapId: number | undefined = yield select<MapIdState>(mapIdSelector) ?? undefined;

  if (!clientId) {
    return;
  }

  const { multiPolygon, boundaryId, boundaryGroupId } = action.payload;

  if (!multiPolygon.length) {
    return;
  }

  yield put(boundaryPolygonSaveStarted());

  try {
    const polygonsWithoutIntersections = flatten(multiPolygon.map(polygon =>
      removeGeometryPolygonIntersections(convertPolygonToGeometry(polygon)).features));

    const combineGeometryRequest: BoundaryCombineGeometryRequest = {
      include: polygonsWithoutIntersections.map(polygon => ({ type: 'geometry', value: polygon.geometry })),
      subtract: [],
    };

    const newGeometry: BoundaryCombineGeometryResponse = yield call(getBoundaryCombineGeometry, clientId, combineGeometryRequest);

    const response: Awaited<ReturnType<typeof bulkUpdateBoundaryGeometry>> = yield call(bulkUpdateBoundaryGeometry, clientId, [{
      boundary_id: boundaryId,
      geometry: newGeometry.geometry,
      map_id: mapId,
    }]);

    if (!response[0]) {
      yield put(boundaryPolygonSaveError());
      return;
    }
    const boundaryStateItem = createBoundaryStateItemFromBoundaryResponse(response[0], { enforceLabelBoundsRecalc: true });
    yield put(boundaryItemsUpdateItem(boundaryGroupId, boundaryStateItem.boundaryStateItem, boundaryStateItem.etag));
    yield put(boundaryPolygonSaveFinished());
  }
  catch {
    yield put(boundaryPolygonSaveError());
  }
}
